/**
 * frontend/src/player/MiniPlayer.jsx
 * SoundDAO Sprint 6A — persistent mini player (fixed above the bottom nav, safe-area aware;
 * on desktop a bar to the right of the sidebar). Hidden until something is queued.
 */

import React, { useEffect } from 'react';
import { usePlayer } from './PlayerContext';
import './MiniPlayer.css';

export default function MiniPlayer({ withNav }) {
  const { current, status, progress, toggle, next, prev, clear, seek, hasNext, hasPrev, index, queue } = usePlayer();

  useEffect(() => {
    const cls = withNav ? 'has-mini-player' : 'has-mini-player-nonav';
    if (current) document.body.classList.add(cls);
    return () => document.body.classList.remove(cls);
  }, [current, withNav]);

  if (!current) return null;

  const pct = progress.duration > 0 ? Math.min(100, (progress.current / progress.duration) * 100) : 0;
  const isError = status === 'error';
  const playing = status === 'playing' || status === 'loading';

  const onBar = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    if (r.width > 0) seek((e.clientX - r.left) / r.width);
  };

  return (
    <div className={`mini-player ${withNav ? 'mini-player--nav' : 'mini-player--nonav'} ${isError ? 'mini-player--error' : ''}`} role="region" aria-label="Now playing">
      <div className="mini-player-inner">
        <div className="mini-player-row">
          <div className="mini-player-art" aria-hidden="true">{isError ? '⚠' : status === 'loading' ? '⏳' : '🎸'}</div>
          <div className="mini-player-text">
            <div className="mini-player-title">{current.title}</div>
            <div className="mini-player-sub">
              {isError
                ? (hasNext ? 'Unavailable — skipping to the next track' : 'Unavailable')
                : <>
                    {current.artist ? `${current.artist} · ` : ''}
                    <a href={current.archiveUrl} target="_blank" rel="noopener noreferrer">Streaming from the Internet Archive ↗</a>
                  </>}
            </div>
          </div>
          <div className="mini-player-ctl">
            <button type="button" onClick={prev} disabled={!hasPrev && !progress.current} aria-label="Previous track">⏮</button>
            <button type="button" className="mini-player-pp" onClick={toggle} disabled={isError} aria-label={playing ? 'Pause' : 'Play'}>
              {status === 'loading' ? '…' : playing ? '❚❚' : '▶'}
            </button>
            <button type="button" onClick={next} disabled={!hasNext} aria-label="Next track">⏭</button>
            <button type="button" className="mini-player-close" onClick={clear} aria-label="Close player">✕</button>
          </div>
        </div>
        <div className="mini-player-bar" onClick={onBar} role="presentation">
          <i style={{ width: `${pct}%` }} />
        </div>
        {queue.length > 1 && <div className="mini-player-count">{index + 1} of {queue.length}</div>}
      </div>
    </div>
  );
}
